import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminListBlogs, adminDeleteBlog } from '../../services/blogs';
import { getAdminToken, clearAdminSession } from '../../services/adminSession';
import BlogEditor from './BlogEditor';

const Ic = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const ICONS = {
  add:     'M12 5v14M5 12h14',
  edit:    ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7','M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
  del:     ['M3 6h18','M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 0-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
  ext:     ['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6','M15 3h6v6','M10 14L21 3'],
  refresh: ['M23 4v6h-6','M1 20v-6h6','M3.51 9a9 9 0 0 1 14.85-3.36L23 10','M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
};

const STATUS_STYLE = {
  draft:     { borderColor: 'var(--amber)',   color: 'var(--amber)'   },
  published: { borderColor: 'var(--green)',   color: 'var(--green)'   },
  archived:  { borderColor: 'var(--text-3)',  color: 'var(--text-3)'  },
};

const ACCESS_STYLE = {
  public:        { borderColor: 'var(--accent)',  color: 'var(--accent)'  },
  authenticated: { borderColor: 'var(--accent-2)',color: 'var(--accent-2)'},
  premium:       { borderColor: 'var(--amber)',   color: 'var(--amber)'   },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { year:'numeric', month:'short', day:'numeric' }) : '—';

const BlogManager = () => {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [statusFilter,setStatusFilter]= useState('all');
  const [page,        setPage]        = useState(0);
  const [total,       setTotal]       = useState(0);
  const [editing,     setEditing]     = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const PER_PAGE = 25;
  const navigate = useNavigate();

  const handleAuthFail = useCallback(() => {
    clearAdminSession();
    navigate('/admin/signin?expired=1', { replace: true });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const t = getAdminToken();
    if (!t) { handleAuthFail(); return; }
    try {
      setLoading(true); setError('');
      const res = await adminListBlogs(t, {
        page: page + 1, limit: PER_PAGE,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        q: search || undefined,
      });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      if (err?.response?.status === 401) { handleAuthFail(); return; }
      setError(err?.response?.data?.message || 'Failed to load blogs');
    } finally { setLoading(false); }
  }, [page, PER_PAGE, statusFilter, search, handleAuthFail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    const t = getAdminToken();
    if (!t) { handleAuthFail(); return; }
    try {
      await adminDeleteBlog(t, confirmDel._id);
      setConfirmDel(null);
      fetchData();
    } catch (err) {
      if (err?.response?.status === 401) { handleAuthFail(); return; }
      setError(err?.response?.data?.message || 'Delete failed');
      setConfirmDel(null);
    }
  };

  if (editing) {
    return (
      <BlogEditor
        token={getAdminToken()}
        blogId={editing.mode === 'edit' ? editing.id : null}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchData(); }}
      />
    );
  }

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <>
      <div className="adm-section-hd">
        <h2>Blogs</h2>
        <p>Create, edit and publish blog posts. Manage visibility and access levels.</p>
      </div>

      {/* Toolbar */}
      <div className="adm-card adm-filter-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="adm-search-input"
            style={{ maxWidth: 260 }}
            placeholder="Search title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchData(); } }}
          />
          <label className="adm-field" style={{ marginBottom: 0, flexShrink: 0 }}>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              style={{ padding: '7px 10px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--rs)', color: 'var(--text)', fontFamily: 'var(--fb)', fontSize: 13 }}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <button className="adm-icon-btn" title="Refresh" onClick={fetchData}><Ic d={ICONS.refresh} /></button>
          <div style={{ flex: 1 }} />
          <button className="adm-btn adm-btn-accent" onClick={() => setEditing({ mode: 'create' })}>
            <Ic d={ICONS.add} size={15} /> New Blog
          </button>
        </div>
      </div>

      <div className="adm-table-bar">
        <span className="adm-pill">{total} blog{total !== 1 ? 's' : ''}</span>
      </div>

      {error && <div className="adm-alert adm-alert-err" style={{ marginBottom: 12 }}>{error}<button onClick={() => setError('')}>✕</button></div>}

      {loading
        ? <div className="adm-spin-wrap"><div className="adm-spinner" /></div>
        : <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Title</th><th>Slug</th><th>Category</th><th>Status</th><th>Access</th><th>Views</th><th>Updated</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0
                  ? <tr><td colSpan={8} className="adm-empty-cell">No blogs yet. Create your first one.</td></tr>
                  : items.map(b => (
                    <tr key={b._id}>
                      <td className="adm-trunc" title={b.title} style={{ maxWidth: 280, fontWeight: 600, color: 'var(--text)' }}>{b.title}</td>
                      <td style={{ fontFamily: 'var(--fm)', fontSize: 12, color: 'var(--text-2)' }}>{b.slug}</td>
                      <td>{b.category || '—'}</td>
                      <td>
                        <span className="adm-status" style={STATUS_STYLE[b.status] || {}}>
                          {(b.status || 'draft').toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className="adm-status" style={ACCESS_STYLE[b.access_level] || {}}>
                          {(b.access_level || 'public').toUpperCase()}
                        </span>
                      </td>
                      <td>{b.view_count || 0}</td>
                      <td className="adm-nowrap">{fmt(b.updated_at)}</td>
                      <td>
                        <div className="adm-row-btns">
                          {b.status === 'published' && (
                            <button className="adm-icon-btn" title="Open public page"
                              onClick={() => window.open(`/blog/${b.slug}`, '_blank')}>
                              <Ic d={ICONS.ext} />
                            </button>
                          )}
                          <button className="adm-icon-btn" title="Edit"
                            onClick={() => setEditing({ mode: 'edit', id: b._id })}>
                            <Ic d={ICONS.edit} />
                          </button>
                          <button className="adm-icon-btn" title="Delete"
                            style={{ '--icon-hover-bg': 'rgba(255,77,106,0.12)', '--icon-hover-border': 'var(--red)', '--icon-hover-color': 'var(--red)' }}
                            onClick={() => setConfirmDel(b)}>
                            <Ic d={ICONS.del} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
      }

      {totalPages > 1 && (
        <div className="adm-pager">
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button className="adm-btn adm-btn-ghost adm-btn-xs" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Delete confirm dialog */}
      {confirmDel && (
        <div className="adm-overlay" onClick={() => setConfirmDel(null)}>
          <div className="adm-dialog adm-dialog-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-dlg-hd">
              <h3>Delete Blog?</h3>
              <button className="adm-dlg-close" onClick={() => setConfirmDel(null)}>✕</button>
            </div>
            <div className="adm-dlg-body">
              <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text)' }}>"{confirmDel.title}"</strong> will be permanently removed. This action cannot be undone.
              </p>
            </div>
            <div className="adm-dlg-ft">
              <button className="adm-btn adm-btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="adm-btn" style={{ background: 'var(--red)', color: '#fff', border: 'none' }} onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogManager;
